// Stay carousel: arrow buttons for a sideways scrolling list ([data-carousel-list]). The arrows
// ([data-carousel-arrows], hidden in the markup) show only while the list overflows and never under
// 750px, where the list is swiped. Each press moves about one screen of cards; an arrow is disabled at
// its end. With a mouse, the row can also be dragged sideways (.is-draggable while it overflows, .is-dragging
// while held, snapping switched off until it settles on the nearest card); a drag never counts as a click.
// The vertical wheel is left to the page. Selecting a card block in the theme editor scrolls it into view.
// Safe to load twice.
// Ingredient cards with a hidden brief ([data-ingredient-card]): on touch screens a tap opens one (.is-open,
// one at a time).
// Opt-in, by markup only (lists without it behave as before):
// - Pagination dots ([data-carousel-dots] with one [data-carousel-dot] button per card, hidden in the markup):
//   shown while the list overflows; the dot of the card nearest the start is aria-current; a dot scrolls to its card.
// - Equal cards ([data-carousel-equal-cards] on the list): the cards keep the height of the tallest closed card
//   (--carousel-card-height, .has-card-height), so a card whose disclosure opens grows alone.
// - Stage ([data-carousel-stage] on the list, its cards' places in data-stay-stage-pos; V6, the vet profiles): from
//   990px one card is active in the middle and the previous and next stand behind it (base.css data-stay-stage
//   places and moves them, once the list is .is-staged); below 990px the list scrolls as usual and the active card
//   is the one snapped to the start. The arrows and dots are always shown; the arrows, dots, the left / right keys
//   (focus inside the carousel) and a click on a card behind change the active card; the change is announced in
//   [data-carousel-status] (a polite live region) only when the visitor made it. [data-carousel-loop]: the arrows
//   and keys go round from the last card to the first. The card's [data-carousel-name] is read out with its label.
//   The list is .is-staged while the stage is on (from 990px), so base.css places the cards only then.
//   data-carousel-stage="always" (V6, the PDP soft chew base's cards on phones): the stage at every width, and a
//   sideways swipe (touch or pen, past 40px and more than 1.5 × the vertical distance) moves to the next / previous
//   card; the page still scrolls up and down over it.
//   Every change of the active card dispatches "stay-carousel:change" (detail.index) on the carousel, and goTo(index,
//   announce) is the way in, so a section can tie its own controls to the stage (expect-hotspots.js's dots).
// - Keyboard focus ([data-carousel-focus] on the list; V6, The science page's chapter links): a card that gets
//   keyboard focus and is not wholly in view (inside the list's scroll-padding) is scrolled just into it; browsers
//   leave a partly visible one where it is.
if (!customElements.get('stay-carousel')) {
  customElements.define(
    'stay-carousel',
    class StayCarousel extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-carousel-list]');
        this.arrows = this.querySelector('[data-carousel-arrows]');
        this.prev = this.querySelector('[data-carousel-prev]');
        this.next = this.querySelector('[data-carousel-next]');
        this.dotsWrap = this.querySelector('[data-carousel-dots]');
        this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
        if (!this.list) return;

        this.desktop = window.matchMedia('(min-width: 750px)');
        this.update = this.update.bind(this);
        this.list.addEventListener('scroll', this.update, { passive: true });
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.list);
        this.desktop.addEventListener('change', this.update);

        this.prev?.addEventListener('click', () => this.move(-1));
        this.next?.addEventListener('click', () => this.move(1));

        document.addEventListener('shopify:block:select', (event) => {
          if (this.list.contains(event.target)) {
            event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        });
        this.initIngredientCards();
        this.initDrag();
        this.initFocus();
        this.initDots();
        this.initStage();
        this.update();
        // Web fonts can change the cards' height without changing the width
        if (this.list.hasAttribute('data-carousel-equal-cards')) document.fonts?.ready.then(() => this.equalCards());
      }

      initDots() {
        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            if (this.stage) {
              this.goTo(index, true);
              return;
            }
            const card = this.list.children[index];
            if (!card) return;
            const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
            const left = this.list.scrollLeft + card.getBoundingClientRect().left - this.list.getBoundingClientRect().left - inset;
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            this.list.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
          });
        });
      }

      // The cards' height while closed: each card's natural height less an open disclosure's text, the tallest of
      // them for all. Measured with the cards unstretched, in the same frame, so nothing shows in between.
      equalCards() {
        if (!this.list.hasAttribute('data-carousel-equal-cards')) return;
        const width = this.list.clientWidth;
        const cards = [...this.list.children];
        this.list.classList.remove('has-card-height');
        this.list.classList.add('is-measuring');
        let tallest = 0;
        cards.forEach((card) => {
          const open = card.querySelector('details[open] [data-stay-disclose-panel]');
          tallest = Math.max(tallest, card.offsetHeight - (open ? open.offsetHeight : 0));
        });
        this.list.classList.remove('is-measuring');
        this.list.style.setProperty('--carousel-card-height', `${tallest}px`);
        this.list.classList.add('has-card-height');
        this.measuredWidth = width;
      }

      initDrag() {
        let startX = 0;
        let startScroll = 0;
        let pointer = null;
        let dragged = false;

        this.list.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || !this.list.classList.contains('is-draggable')) return;
          pointer = event.pointerId;
          startX = event.clientX;
          startScroll = this.list.scrollLeft;
          dragged = false;
        });

        this.list.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointer) return;
          const distance = event.clientX - startX;
          if (!dragged && Math.abs(distance) < 6) return;
          if (!dragged) {
            dragged = true;
            this.list.setPointerCapture(pointer);
            this.list.classList.add('is-dragging');
          }
          this.list.scrollLeft = startScroll - distance;
        });

        const release = (event) => {
          if (event.pointerId !== pointer) return;
          pointer = null;
          if (!dragged) return;
          this.settle();
        };
        this.list.addEventListener('pointerup', release);
        this.list.addEventListener('pointercancel', release);

        // A drag ends with a click on the card under the mouse: swallow it
        this.list.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            dragged = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
        this.list.addEventListener('dragstart', (event) => event.preventDefault());
      }

      initFocus() {
        if (!this.list.hasAttribute('data-carousel-focus')) return;
        this.list.addEventListener('focusin', (event) => {
          if (!event.target.matches(':focus-visible')) return;
          const card = [...this.list.children].find((child) => child.contains(event.target));
          if (!card) return;
          const style = getComputedStyle(this.list);
          const box = this.list.getBoundingClientRect();
          const rect = card.getBoundingClientRect();
          const start = box.left + (parseFloat(style.scrollPaddingLeft) || 0);
          const end = box.right - (parseFloat(style.scrollPaddingRight) || 0);
          let left = 0;
          if (rect.left < start) left = rect.left - start;
          else if (rect.right > end) left = rect.right - end;
          if (!left) return;
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.list.scrollBy({ left, behavior: reduce ? 'auto' : 'smooth' });
        });
      }

      // After a drag: glide to the nearest card start, then hand back to scroll snapping. A start past the end
      // counts as the end, so a row that overflows by less than a card can still be dragged to it.
      settle() {
        const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
        const listLeft = this.list.getBoundingClientRect().left;
        const max = this.list.scrollWidth - this.list.clientWidth;
        let target = this.list.scrollLeft;
        let best = Infinity;
        [...this.list.children].forEach((card) => {
          const start = this.list.scrollLeft + card.getBoundingClientRect().left - listLeft - inset;
          const left = Math.max(0, Math.min(max, start));
          if (Math.abs(left - this.list.scrollLeft) < best) {
            best = Math.abs(left - this.list.scrollLeft);
            target = left;
          }
        });
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.list.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.list.classList.remove('is-dragging'), reduce ? 0 : 600);
      }

      initIngredientCards() {
        const cards = [...this.list.querySelectorAll('[data-ingredient-card]')];
        if (!cards.length) return;
        cards.forEach((card) => {
          card.addEventListener('pointerup', (event) => {
            if (event.pointerType !== 'touch') return;
            const open = !card.classList.contains('is-open');
            cards.forEach((other) => other.classList.toggle('is-open', other === card && open));
          });
        });
      }

      initStage() {
        this.stage = this.list.hasAttribute('data-carousel-stage');
        if (!this.stage) return;
        this.loop = this.list.hasAttribute('data-carousel-loop');
        this.status = this.querySelector('[data-carousel-status]');
        this.always = this.list.dataset.carouselStage === 'always';
        this.staged = window.matchMedia(this.always ? 'all' : '(min-width: 990px)');
        this.active = Math.max(0, this.cards().findIndex((card) => card.dataset.stayStagePos === 'active'));
        this.list.classList.toggle('is-staged', this.staged.matches);

        // Into the stage: the places from the active card; out of it: the row scrolled to the active card
        this.staged.addEventListener('change', () => {
          this.list.classList.toggle('is-staged', this.staged.matches);
          if (this.staged.matches) this.place();
          else this.scrollToCard(this.active, 'auto');
          this.update();
        });

        if (this.always) this.initSwipe();

        this.addEventListener('keydown', (event) => {
          if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        });

        // A card behind the active one comes forward when clicked
        this.list.addEventListener('click', (event) => {
          if (!this.staged.matches) return;
          const card = event.target.closest('[data-stay-stage-pos]');
          if (!card || card.dataset.stayStagePos === 'active') return;
          this.goTo(this.cards().indexOf(card), true);
        });

        document.addEventListener('shopify:block:select', (event) => {
          const index = this.cards().indexOf(event.target);
          if (index > -1) this.goTo(index, false);
        });
      }

      initSwipe() {
        let start = null;
        this.list.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse') return;
          start = { x: event.clientX, y: event.clientY, id: event.pointerId };
        });
        const end = (event) => {
          if (!start || event.pointerId !== start.id) return;
          const dx = event.clientX - start.x;
          const dy = event.clientY - start.y;
          start = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          // A touch that moved usually fires no click; if one comes, it isn't a tap on the card behind
          this.swiped = true;
          setTimeout(() => {
            this.swiped = false;
          }, 400);
          this.move(dx < 0 ? 1 : -1);
        };
        this.list.addEventListener('pointerup', end);
        this.list.addEventListener('pointercancel', () => {
          start = null;
        });
        this.list.addEventListener(
          'click',
          (event) => {
            if (this.swiped) event.stopPropagation();
          },
          true
        );
      }

      cards() {
        return [...this.list.children];
      }

      // Each card's place in the stage, counted round from the active card
      place() {
        const cards = this.cards();
        const total = cards.length;
        cards.forEach((card, index) => {
          const step = (index - this.active + total) % total;
          let pos = 'hidden';
          if (step === 0) pos = 'active';
          else if (step === 1) pos = 'next';
          else if (step === total - 1) pos = 'prev';
          card.dataset.stayStagePos = pos;
        });
        this.dots.forEach((dot, index) => {
          if (index === this.active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.dispatchEvent(new CustomEvent('stay-carousel:change', { detail: { index: this.active } }));
      }

      goTo(index, announce) {
        const total = this.cards().length;
        if (!total) return;
        this.active = this.loop ? (index + total) % total : Math.max(0, Math.min(total - 1, index));
        if (this.staged.matches) this.place();
        else this.scrollToCard(this.active);
        if (announce && this.status) {
          const card = this.cards()[this.active];
          const name = card.querySelector('[data-carousel-name]')?.textContent.trim();
          this.status.textContent = [card.getAttribute('aria-label'), name].filter(Boolean).join(': ');
        }
      }

      scrollToCard(index, behavior) {
        const card = this.cards()[index];
        if (!card) return;
        const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
        const left = this.list.scrollLeft + card.getBoundingClientRect().left - this.list.getBoundingClientRect().left - inset;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.list.scrollTo({ left, behavior: behavior || (reduce ? 'auto' : 'smooth') });
      }

      // Below 990px: the active card is the one nearest the start (the last at the row's end)
      updateStage() {
        const overflow = this.list.scrollWidth > this.list.clientWidth + 1;
        this.list.classList.toggle('is-draggable', overflow && !this.staged.matches);
        if (this.staged.matches) return;
        const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
        const listLeft = this.list.getBoundingClientRect().left + inset;
        const cards = this.cards();
        let active = 0;
        let best = Infinity;
        cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - listLeft);
          if (distance < best) {
            best = distance;
            active = index;
          }
        });
        if (overflow && this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 1) active = cards.length - 1;
        if (active === this.active) return;
        this.active = active;
        this.place();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.desktop?.removeEventListener('change', this.update);
      }

      move(direction) {
        if (this.stage) {
          this.goTo(this.active + direction, true);
          return;
        }
        const card = this.list.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
        const step = card ? card.getBoundingClientRect().width + gap : this.list.clientWidth;
        const cards = Math.max(1, Math.floor((this.list.clientWidth * 0.8) / step));
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.list.scrollBy({ left: direction * cards * step, behavior: reduce ? 'auto' : 'smooth' });
      }

      update() {
        // Equal cards: again when the width changes, not when a card opens (that changes only the height)
        if (this.list.clientWidth !== this.measuredWidth) this.equalCards();
        if (this.stage) {
          this.updateStage();
          return;
        }
        const overflow = this.list.scrollWidth > this.list.clientWidth + 1;
        this.list.classList.toggle('is-draggable', overflow);
        if (this.dotsWrap) {
          this.dotsWrap.hidden = !overflow;
          const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
          const listLeft = this.list.getBoundingClientRect().left + inset;
          const atEnd = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 1;
          let active = 0;
          let best = Infinity;
          [...this.list.children].forEach((card, index) => {
            const distance = Math.abs(card.getBoundingClientRect().left - listLeft);
            if (distance < best) {
              best = distance;
              active = index;
            }
          });
          if (atEnd && this.list.scrollLeft > 1) active = this.dots.length - 1;
          this.dots.forEach((dot, index) => {
            if (index === active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }
        if (!this.arrows) return;
        this.arrows.hidden = !overflow || !this.desktop.matches;
        const max = this.list.scrollWidth - this.list.clientWidth - 1;
        if (this.prev) this.prev.disabled = this.list.scrollLeft <= 1;
        if (this.next) this.next.disabled = this.list.scrollLeft >= max;
      }
    }
  );
}
